import { matchPath } from 'react-router-dom';
import { fetchFeatureFlags } from '../actions/featureFlags';
import { fragno1Resolved } from '../actions/fragno1';
import { sessionCheck } from '../actions/session';
import { FLAG_CACHE_KEY } from '../constants';
import i18n from '../i18n';
import routes from '../routing/routes';
import { reportWarning, sendToSentry, setSentryTag } from '../utils/error';
import { readCache, writeCache } from '../utils/storage';
import { applyBrandCopy } from './brandCopy';
import { applyFavicon } from './favicon';

export const FLAG_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;
export const FLAG_DEADLINE_MS = 3000;
// The flag is on in production for good, so a boot that can't read it renders Fragno 1.
export const FLAG_FALLBACK = true;

export const requiresAuth = (pathname) =>
  routes.some(
    (route) =>
      route.requireAuth &&
      matchPath(pathname, { exact: route.exact, path: route.path }) !== null,
  );

const readCachedFlag = () => {
  const cached = readCache(FLAG_CACHE_KEY, FLAG_CACHE_MAX_AGE_MS);
  return typeof cached === 'boolean' ? cached : null;
};

// Logged out, /feature_flags returns the global value, not the user's. isLoggedIn stays true
// through sign-out, so isLoggingOut keeps a late response from refilling the cache sessionEnd cleared.
const cacheFlagIfLoggedIn = (store, flags, session) => {
  if (!session) return;
  Promise.all([flags, session])
    .then(() => {
      const { error, fragno1 } = store.getState().featureFlags;
      const { isLoggedIn, isLoggingOut } = store.getState().session;
      if (!error && isLoggedIn && !isLoggingOut) {
        writeCache(FLAG_CACHE_KEY, fragno1);
      }
    })
    .catch(sendToSentry);
};

const readFlag = (store) => {
  const { error, fragno1 } = store.getState().featureFlags;
  return error
    ? { on: FLAG_FALLBACK, source: 'failed' }
    : { on: fragno1, source: 'flags' };
};

// The flag once /feature_flags and /session land, or at the deadline. Waiting for the session
// too means AuthRoute has it at first render, so its LoadingPage doesn't follow the boot spinner.
// A signed-out session settles it without the flag: the login redirect cancels /feature_flags,
// and a stale session cookie gets a 401 from it, so neither is a flags failure.
const flagOrDeadline = (store, flags, session) =>
  new Promise((resolve) => {
    let flagsLanded = false;
    flags.then(() => {
      flagsLanded = true;
    });
    const deadline = setTimeout(
      () =>
        resolve(
          flagsLanded
            ? readFlag(store)
            : { on: FLAG_FALLBACK, source: 'deadline' },
        ),
      FLAG_DEADLINE_MS,
    );
    session.then(
      () => {
        if (!store.getState().session.isLoggedIn) {
          clearTimeout(deadline);
          resolve({ on: FLAG_FALLBACK, source: 'signed-out' });
        }
      },
      () => {},
    );
    Promise.all([flags, session.catch(() => {})]).then(() => {
      clearTimeout(deadline);
      resolve(readFlag(store));
    });
  });

// Resolves { on, source }: source is cache | public-default | signed-out | flags | failed | deadline.
export const loadFragno1Flag = (store) => {
  const authed = requiresAuth(window.location.pathname);
  // AuthRoute skips its own check while this one is in flight.
  const session = authed ? store.dispatch(sessionCheck()) : null;
  const flags = store.dispatch(fetchFeatureFlags());
  cacheFlagIfLoggedIn(store, flags, session);

  const cached = readCachedFlag();
  if (cached !== null) return Promise.resolve({ on: cached, source: 'cache' });
  if (!authed) {
    return Promise.resolve({ on: FLAG_FALLBACK, source: 'public-default' });
  }
  return flagOrDeadline(store, flags, session);
};

const warnFallback = (store, source) => {
  if (source !== 'deadline' && source !== 'failed') return;
  reportWarning(
    `Fragno 1 boot: feature flags ${source}; rendered the fallback`,
    {
      extras:
        source === 'deadline'
          ? { deadlineMs: FLAG_DEADLINE_MS }
          : { error: store.getState().featureFlags.error },
      fingerprint: ['fragno1-boot', `flags-${source}`],
    },
  );
};

// Resolves the flag into the store; never rejects, so the app always renders.
const bootFragno1 = (store) =>
  Promise.resolve()
    .then(() => loadFragno1Flag(store))
    .catch((error) => {
      sendToSentry(error);
      return { on: FLAG_FALLBACK, source: 'failed' };
    })
    .then(({ on, source }) => {
      setSentryTag('fragno1_source', source);
      warnFallback(store, source);
      if (on) applyBrandCopy(i18n);
      store.dispatch(fragno1Resolved({ on, source }));
      applyFavicon(on);
    });

export default bootFragno1;
